import { useState } from 'react';
import { ShieldCheck, Loader2, X, AlertTriangle } from 'lucide-react';

interface ReauthModalProps {
  isOpen: boolean;
  onSuccess: (token: string, expiresInSeconds: number) => void;
  onCancel: () => void;
}

export function ReauthModal({ isOpen, onSuccess, onCancel }: ReauthModalProps) {
  const [mfaCode, setMfaCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const response = await fetch('/api/v1/admin/auth/reauth', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ mfaCode }),
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type') ?? '';
        if (contentType.includes('application/json')) {
          const data = await response.json();
          throw new Error(data.message || 'Re-authentication failed');
        }

        const text = await response.text();
        throw new Error(text || 'Re-authentication failed');
      }

      const data = await response.json();
      setMfaCode('');
      onSuccess(data.reauthToken, data.expiresInSeconds);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Re-authentication failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setMfaCode('');
    setError(null);
    onCancel();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-slate-900 border border-slate-700 rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-900/30 rounded-lg">
              <ShieldCheck className="h-5 w-5 text-amber-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">
                Verify Your Identity
              </h2>
              <p className="text-sm text-slate-400">
                This action requires additional verification
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-1 rounded hover:bg-slate-800 text-slate-400 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="mb-4 p-3 bg-amber-900/20 border border-amber-800/50 rounded-lg">
            <div className="flex items-start gap-2 text-amber-400">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <p className="text-sm">
                You are about to perform a sensitive operation. Please enter your
                MFA code to continue.
              </p>
            </div>
          </div>

          <div className="mb-6">
            <label
              htmlFor="mfaCode"
              className="block text-sm font-medium text-slate-300 mb-2"
            >
              MFA Code
            </label>
            <input
              id="mfaCode"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
              placeholder="Enter 6-digit code"
              autoFocus
              autoComplete="one-time-code"
              className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white text-center text-2xl tracking-[0.5em] font-mono placeholder:text-slate-500 placeholder:text-base placeholder:tracking-normal focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
            />
            {error && (
              <p className="mt-2 text-sm text-red-400">{error}</p>
            )}
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mfaCode.length !== 6 || isLoading}
              className="flex-1 px-4 py-2 bg-cyan-600 hover:bg-cyan-500 disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                <>
                  <ShieldCheck className="h-4 w-4" />
                  Verify
                </>
              )}
            </button>
          </div>
        </form>

        {/* Footer hint */}
        <div className="px-6 pb-4">
          <p className="text-xs text-slate-500 text-center">
            Enter your 6-digit authenticator code
          </p>
        </div>
      </div>
    </div>
  );
}
